/**
 * WaiverFlow — THE single shared waiver-signing wizard.
 * Used by BOTH:
 *   - MobileWaiverWizard (customer-ticket overlay, booking mode)
 *   - StandaloneWaiverPage (/sign/{org}, walk-up mode)
 * Maintain the flow HERE — the two wrappers only supply branding, the submit
 * endpoint, and what happens after success.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ChevronLeft, Shield, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import api from '../utils/api';
import PhoneInput from './PhoneInput';
import { NATIONALITIES } from '../utils/nationalities';

const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MIN_NAME_LENGTH = 2;
const RESEND_COOLDOWN_SECONDS = 15;
const AGE_RANGES = ['Under 18', '18-24', '25-34', '35-44', '45-54', '55+'];

// UAE bilingual helpers — Arabic line rendered under (or beside) the English text
const Ar = ({ children, className = '' }) => (
  <span dir="rtl" lang="ar" className={`block ${className}`}>{children}</span>
);
const ArInline = ({ children }) => (
  <> &middot; <span dir="rtl" lang="ar">{children}</span></>
);

const sanitizeInput = (str, maxLength = 100) => {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '').replace(/[<>'"&]/g, '').slice(0, maxLength);
};
const sanitizeInputFinal = (str, maxLength = 100) => {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '').replace(/[<>'"&]/g, '').trim().slice(0, maxLength);
};
const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email && re.test(email) && email.length <= MAX_EMAIL_LENGTH;
};

// 6-digit OTP input — supports iOS "from Messages" autofill (one-time-code) and
// pasting: any multi-digit input gets distributed across the boxes, and the
// waiver auto-submits the moment the 6th digit lands.
const CodeInput = ({ value, onChange, disabled = false, onComplete }) => {
  const inputRefs = useRef([]);
  const digits = value.padEnd(6, '').split('').slice(0, 6);

  const commit = (newValue) => {
    onChange(newValue);
    inputRefs.current[Math.min(newValue.length, 5)]?.focus();
    if (newValue.length === 6 && onComplete) setTimeout(() => onComplete(newValue), 100);
  };

  const handleChange = (index, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 1) {
      // iOS SMS autofill / fast typing dumps multiple digits into one box
      commit((value.slice(0, index) + raw).slice(0, 6));
      return;
    }
    const newDigits = [...digits];
    newDigits[index] = raw;
    const newValue = newDigits.join('');
    onChange(newValue);
    if (raw && index < 5) inputRefs.current[index + 1]?.focus();
    if (newValue.length === 6 && onComplete) setTimeout(() => onComplete(newValue), 100);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    commit(e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6));
  };

  return (
    <div className="flex gap-2 justify-center mb-6">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input key={i} ref={(el) => (inputRefs.current[i] = el)}
          type="text" inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          value={digits[i] || ''} onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)} onPaste={handlePaste}
          disabled={disabled} autoFocus={i === 0}
          data-testid={`waiver-otp-digit-${i}`}
          className="h-16 w-[50px] rounded-2xl border border-gray-200 bg-gray-50 text-center font-mono text-3xl outline-none transition-all focus:border-blue-600 focus:bg-white focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100"
        />
      ))}
    </div>
  );
};

/**
 * Props:
 *   waiverText     — the waiver body to display
 *   branding       — { name, logoUrl, brandColor, waiverVersion }
 *   layout         — 'overlay' (fixed full-screen modal) | 'page' (full page)
 *   onClose        — overlay close handler (overlay layout only)
 *   onSubmit       — async (commonPayload) => void; wrapper posts to its endpoint
 *   successAction  — 'reload' (5s countdown then window reload) | 'reset' (walk-up: "Sign Another Waiver")
 */
const WaiverFlow = ({ waiverText, branding = {}, layout = 'overlay', onClose, onSubmit, successAction = 'reload', region = 'USA', hideBranding = false, kioskBooking = null, posFields = null }) => {
  const brandColor = branding.brandColor || '#2563EB';
  const isUAE = region === 'UAE';

  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const signaturePadRef = useRef(null);
  const [capturedSignatureData, setCapturedSignatureData] = useState(null);
  const [hasAgreedToWaiver, setHasAgreedToWaiver] = useState(false);
  // 🖥️ POS signers land STRAIGHT on the waiver text — no "review the waiver" intro
  const [showWaiverText, setShowWaiverText] = useState(!!posFields);
  const [verificationMethod, setVerificationMethod] = useState('');
  const [phoneE164, setPhoneE164] = useState('');
  const [phoneValid, setPhoneValid] = useState(false);
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [sentChannels, setSentChannels] = useState([]);
  const [sendingCode, setSendingCode] = useState(false);
  const [signing, setSigning] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [skippedVerification, setSkippedVerification] = useState(false);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [ageRange, setAgeRange] = useState('');
  const [nationality, setNationality] = useState(isUAE ? 'AE' : 'US');
  const [emailOptIn, setEmailOptIn] = useState(false);  // UAE only — express email consent (PDPL: must be unchecked by default); US email consent is notice-based
  const [smsOptIn, setSmsOptIn] = useState(false);      // SMS/WhatsApp always needs express opt-in (TCPA / PDPL)
  const [pendingSig, setPendingSig] = useState(null);
  const latestOtpRef = useRef('');
  // Bumped on "+ add another signer" — remounts the scroll body so signer #2
  // ALWAYS starts at the very top with a fresh signature pad (scroll-trap fix).
  const [signerSession, setSignerSession] = useState(0);
  // 🖥️ POS (kiosk walk-ins) — extra fields collected at signing per the org's
  // Walk-Ins POS config (required | optional | hidden). Null = standard flow.
  const [posEmail, setPosEmail] = useState('');
  const [posPhone, setPosPhone] = useState('');
  const [posPhoneValid, setPosPhoneValid] = useState(false);
  const [posIdNumber, setPosIdNumber] = useState('');
  const posShow = (k) => !!(posFields && posFields[k] && posFields[k] !== 'hidden');
  const posReq = (k) => !!(posFields && posFields[k] === 'required');
  // OTP path already collected + verified email & phone — don't re-ask at the final step
  const posContactCollected = ['sms', 'whatsapp', 'email'].includes(pendingSig?.verificationMethod);

  const fullPhone = phoneE164;
  const phoneChannel = isUAE ? 'whatsapp' : 'sms';
  const phoneLabel = isUAE ? 'WhatsApp Number' : 'Phone Number';

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (showSuccess && successAction === 'reload') {
      if (countdown > 0) {
        const t = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(t);
      }
      window.location.reload();
    }
  }, [showSuccess, countdown, successAction]);

  const resetAll = () => {
    setStep(1); setName(''); setCapturedSignatureData(null); setHasAgreedToWaiver(false);
    setShowWaiverText(!!posFields); setVerificationMethod(''); setPhoneE164(''); setPhoneValid(false);
    setEmail(''); setOtpCode(''); setSentChannels([]); setVerificationFailed(false); setResendCooldown(0);
    setSkippedVerification(false); setNameError(''); setEmailError('');
    setShowSuccess(false); setCountdown(5);
    setAgeRange(''); setNationality(isUAE ? 'AE' : 'US'); setEmailOptIn(false); setSmsOptIn(false); setPendingSig(null);
    setPosEmail(''); setPosPhone(''); setPosPhoneValid(false); setPosIdNumber('');
    setReadToBottom(false);
    latestOtpRef.current = '';
    // Belt-and-braces for signer #2+ on kiosks: clear any leaked scroll/pointer
    // locks (signature pad / modal libraries) that froze the page after signer #1.
    try {
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
      document.body.style.position = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.overflowY = '';
      document.body.style.pointerEvents = '';
      document.body.style.touchAction = '';
      document.body.removeAttribute('data-scroll-locked');
    } catch { /* noop */ }
  };

  const scrollBodyRef = useRef(null);
  // Every step change (and every new signer session) starts at the top —
  // signer #2/#3 on a kiosk previously inherited signer #1's scroll position.
  useEffect(() => {
    const toTop = () => {
      try { if (scrollBodyRef.current) scrollBodyRef.current.scrollTop = 0; } catch { /* noop */ }
      try { window.scrollTo(0, 0); } catch { /* noop */ }
      try { const se = document.scrollingElement; if (se) se.scrollTop = 0; } catch { /* noop */ }
    };
    toTop();
    const t = setTimeout(toTop, 60); // again after layout settles (iPad Safari)
    // Clear leaked scroll locks on every step transition too (multi-signer safety).
    // Radix UI Popover (used in PhoneInput) applies scroll locks that must be cleared.
    try {
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
      document.body.style.position = '';
      document.body.style.pointerEvents = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.overflowY = '';
      // Also remove data-scroll-locked attribute if present
      document.body.removeAttribute('data-scroll-locked');
    } catch { /* noop */ }
    return () => clearTimeout(t);
  }, [step, showSuccess, signerSession]);

  // Scroll-to-the-end gate: the waiver renders FULL height (no inner scrollbox) —
  // the agree checkbox unlocks only once the reader reaches the end of the text.
  // IntersectionObserver on an end-sentinel works no matter which container scrolls.
  const [readToBottom, setReadToBottom] = useState(false);
  const waiverEndRef = useRef(null);
  useEffect(() => {
    if (!showWaiverText) { setReadToBottom(false); return undefined; }
    const el = waiverEndRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setReadToBottom(true); return undefined; }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setReadToBottom(true);
    }, { threshold: 0.9 });
    obs.observe(el);
    return () => obs.disconnect();
    // signerSession/step: re-arm on the NEW sentinel after "+ add another signer"
    // remounts the scroll body — otherwise signer #2's checkbox never unlocks
  }, [showWaiverText, signerSession, step]);

  const validateName = (v) => {
    const s = sanitizeInput(v, MAX_NAME_LENGTH);
    if (s.length < MIN_NAME_LENGTH) { setNameError(isUAE ? 'Name must be at least 2 characters · يجب أن يتكون الاسم من حرفين على الأقل' : 'Name must be at least 2 characters'); return false; }
    if (!/^[a-zA-Z\u0600-\u06FF\s\-'.]+$/.test(s)) { setNameError(isUAE ? 'Name contains invalid characters · يحتوي الاسم على أحرف غير صالحة' : 'Name contains invalid characters'); return false; }
    setNameError(''); return true;
  };
  const validateEmail = (v) => {
    if (!isValidEmail(v)) { setEmailError('Please enter a valid email'); return false; }
    setEmailError(''); return true;
  };

  const captureSignatureLightweight = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) return null;
    try {
      const canvas = signaturePadRef.current.getCanvas();
      const maxW = 300, maxH = 150;
      let w = canvas.width, h = canvas.height;
      if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
      if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
      const c2 = document.createElement('canvas'); c2.width = w; c2.height = h;
      const ctx = c2.getContext('2d');
      ctx.fillStyle = 'white'; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(canvas, 0, 0, w, h);
      return c2.toDataURL('image/jpeg', 0.6);
    } catch { return null; }
  };

  // Channel-aware OTP dispatch — sends only to the contacts provided
  const sendOtpCodes = async (emailVal, phoneVal) => {
    setSendingCode(true); setVerificationFailed(false);
    const jobs = [];
    if (phoneVal) jobs.push({ ch: phoneChannel, req: api.post('/public/sms/send-code', { phoneNumber: phoneVal, channel: phoneChannel }) });
    if (emailVal) jobs.push({ ch: 'email', req: api.post('/public/email/send-code', { email: sanitizeInput(emailVal, MAX_EMAIL_LENGTH) }) });
    const results = await Promise.allSettled(jobs.map((j) => j.req));
    setSendingCode(false);
    const ok = jobs.filter((_, i) => results[i].status === 'fulfilled').map((j) => j.ch);
    results.forEach((r, i) => { if (r.status === 'rejected') console.error(`${jobs[i].ch} send-code failed:`, r.reason?.response?.data || r.reason?.message); });
    if (!ok.length) {
      setVerificationFailed(true);
      toast.error('Failed to send verification code — please check your details');
      return false;
    }
    setSentChannels(ok);
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setSkippedVerification(false);
    toast.success(ok.length === 2
      ? `Code sent to your email and ${isUAE ? 'WhatsApp' : 'phone'} — either one works`
      : `Code sent to your ${ok[0] === 'email' ? 'email' : (isUAE ? 'WhatsApp' : 'phone')}`);
    setStep(5);
    return true;
  };

  const handleSendCode = async () => {
    // 🖥️ POS: only the channels the POS config collects — one valid contact is enough
    if (posFields) {
      const emailVal = posShow('email') && isValidEmail(email) ? email : '';
      const phoneVal = posShow('phone') && phoneValid ? fullPhone : '';
      if (!emailVal && !phoneVal) { toast.error('Please enter a valid contact to receive your code'); return; }
      await sendOtpCodes(emailVal, phoneVal);
      return;
    }
    if (!validateEmail(email)) return;
    if (!phoneValid) { toast.error('Please enter a valid phone number'); return; }
    await sendOtpCodes(email, fullPhone);
  };

  const buildDeviceInfo = () => {
    const safe = (fn) => { try { return fn(); } catch { return 'Unknown'; } };
    return {
      userAgent: safe(() => navigator.userAgent),
      platform: safe(() => navigator.platform),
      screenResolution: safe(() => `${window.screen.width}x${window.screen.height}`),
      timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
      language: safe(() => navigator.language),
    };
  };

  const submitCommon = async (payload) => {
    try {
      const result = await onSubmit(payload);
      try {
        // 🖥️ POS pre-sign: hand the signature id up to the kiosk sheet so it can
        // link signatures to the booking at checkout time.
        if (posFields && window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'BF_WAIVER_SIGNED',
            signatureId: (result && result.signatureId) || null,
            signerName: payload.signerName || '',
            attachedBooking: (result && result.attachedBooking) || null,
          }, '*');
        }
      } catch { /* messaging is best-effort */ }
      setShowSuccess(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 400) toast.error(`Error: ${detail || 'Please check your information.'}`);
      else if (status === 404) toast.error('Not found. Please refresh the page.');
      else if (status === 403) toast.error('Access denied. Please refresh the page.');
      else if (err.request && !err.response) toast.error('Network error. Please check your connection.');
      else toast.error('Failed to sign waiver. Please try again.');
      throw err;
    }
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      const code = latestOtpRef.current || otpCode;
      let verified = false; let verifiedContact = ''; let method = '';

      if (sentChannels.includes('sms') || sentChannels.includes('whatsapp')) {
        try {
          const res = await api.post('/public/sms/verify-code', { phoneNumber: fullPhone, code });
          if (res.data.valid) { verified = true; verifiedContact = fullPhone; method = phoneChannel; }
        } catch { /* fall through to email verification */ }
      }
      if (!verified && sentChannels.includes('email')) {
        try {
          const res = await api.post('/public/email/verify-code', { email: sanitizeInput(email, MAX_EMAIL_LENGTH), code });
          if (res.data.valid) { verified = true; verifiedContact = email; method = 'email'; }
        } catch { /* handled below */ }
      }
      if (!verified) {
        toast.error('Invalid or expired code. Please try again.');
        return;
      }

      setVerificationMethod(method);
      const pending = {
        verified: true,
        verificationMethod: method,
        verifiedContact,
        phoneNumber: fullPhone,
        email: sanitizeInputFinal(email, MAX_EMAIL_LENGTH),
        idVerificationRequired: false,
      };
      setPendingSig(pending);
      setStep(7);
    } finally { setSigning(false); }
  };

  const handleIdVerificationSign = () => {
    const pending = {
      verified: false,
      verificationMethod: 'id',
      verifiedContact: 'ID Required',
      phoneNumber: phoneE164 || '',
      email: email || '',
      idVerificationRequired: true,
    };
    setPendingSig(pending);
    setStep(7);
  };

  const orgLabel = branding.name || 'this operator';
  // Consent snapshots — exact language stored with the signature (compliance record)
  // US email: notice-based (CAN-SPAM). UAE email: express opt-in (PDPL).
  // SMS/WhatsApp: express opt-in everywhere (TCPA / PDPL).
  const emailConsentGiven = isUAE ? emailOptIn : true;
  const emailConsentText = isUAE
    ? `I agree that ${orgLabel} may keep my contact details and email me occasional updates and offers. I can unsubscribe anytime. | أوافق على أن تحتفظ ${orgLabel} ببيانات الاتصال الخاصة بي وأن ترسل لي تحديثات وعروضًا عبر البريد الإلكتروني من حين لآخر. يمكنني إلغاء الاشتراك في أي وقت.`
    : `By signing this waiver, I agree that ${orgLabel} may email me occasional updates and offers. I can unsubscribe anytime.`;
  const smsConsentText = isUAE
    ? `I agree that ${orgLabel} may send me occasional offers and updates on WhatsApp. I can reply STOP to opt out anytime. | أوافق على أن ترسل لي ${orgLabel} عروضًا وتحديثات عبر واتساب من حين لآخر. يمكنني الرد بكلمة STOP لإلغاء الاشتراك في أي وقت.`
    : `I agree that ${orgLabel} may send me occasional offers and updates by text message. Message & data rates may apply. I can reply STOP to opt out anytime. Consent is not a condition of purchase.`;

  const finalizeSign = async () => {
    if (!ageRange) { toast.error('Please select your age range'); return; }
    if (posFields && !posContactCollected) {
      if (posShow('email') && ((posReq('email') && !isValidEmail(posEmail)) || (posEmail && !isValidEmail(posEmail)))) {
        toast.error('Please enter a valid email'); return;
      }
      if (posReq('phone') && !posPhoneValid) { toast.error('Please enter a valid phone number'); return; }
    }
    if (posFields) {
      if (posReq('idNumber') && !posIdNumber.trim()) { toast.error('Please enter an ID / passport number'); return; }
    }
    setSigning(true);
    try {
      await submitCommon({
        signerName: sanitizeInputFinal(name, MAX_NAME_LENGTH),
        signatureData: capturedSignatureData || '',
        ...pendingSig,
        ...(posFields ? {
          email: sanitizeInputFinal(posEmail, MAX_EMAIL_LENGTH) || pendingSig?.email || '',
          phoneNumber: posPhone || pendingSig?.phoneNumber || '',
          idNumber: sanitizeInputFinal(posIdNumber, 120),
        } : {}),
        deviceInfo: buildDeviceInfo(),
        signedAt: new Date().toISOString(),
        ageRange,
        nationality: (posFields && !posShow('nationality')) ? '' : nationality,
        marketingOptIn: emailConsentGiven,
        marketingConsentText: emailConsentGiven ? emailConsentText : '',
        smsMarketingOptIn: smsOptIn,
        smsMarketingConsentText: smsOptIn ? smsConsentText : '',
      });
    } catch { /* toast handled in submitCommon */ }
    finally { setSigning(false); }
  };

  const wrapperCls = layout === 'overlay'
    ? 'fixed inset-0 bg-white z-50 flex flex-col overflow-y-auto'
    : layout === 'embed'
      ? 'h-full bg-white flex flex-col overflow-hidden'
      // 'page': MUST be height-constrained — the scroll body below uses
      // overscroll-contain, which swallows wheel/touch at its boundary when it
      // isn't the real scroller (min-h-screen made the page unscrollable).
      : 'h-dvh bg-white flex flex-col overflow-hidden';

  // ── Success screen ──────────────────────────────────────────────────────────
  if (showSuccess) {
    // 🖥️ POS: compact modal success — "+ add another signer" or straight back to checkout
    if (posFields) {
      return (
        <div className={wrapperCls} data-testid="waiver-success">
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center max-w-sm mx-auto">
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Waiver signed{isUAE && <Ar className="text-lg mt-1">تم توقيع الإقرار</Ar>}</h2>
              <p className="text-sm text-gray-500 mb-7">Thanks, {name}. Anyone else in the group?{isUAE && <Ar className="mt-0.5">شكرًا لك. هل يوجد موقّعون آخرون في المجموعة؟</Ar>}</p>
              <Button onClick={resetAll} data-testid="waiver-success-sign-another"
                variant="outline"
                className="w-full h-12 text-base font-semibold border-gray-200 mb-2.5">
                + Add another signer{isUAE && <ArInline>إضافة موقّع آخر</ArInline>}
              </Button>
              <Button
                onClick={() => { try { window.parent.postMessage({ type: 'BF_POS_WAIVER_DONE' }, '*'); } catch { /* noop */ } }}
                data-testid="waiver-success-pos-done"
                className="w-full h-12 rounded-full text-white text-base font-semibold" style={{ background: brandColor }}>
                Done — back to checkout{isUAE && <ArInline>تم — العودة للدفع</ArInline>}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={wrapperCls} data-testid="waiver-success">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Waiver Signed!{isUAE && <Ar className="text-xl mt-1">تم توقيع الإقرار!</Ar>}</h2>
            <p className="text-gray-600 mb-2">You have successfully signed the waiver.{isUAE && <Ar className="text-sm mt-0.5">لقد وقّعت الإقرار بنجاح.</Ar>}</p>
            <p className="text-sm text-gray-500 mb-8">Thank you, {name}.{isUAE && <> <span dir="rtl" lang="ar">شكرًا لك.</span></>}</p>
            {verificationMethod === 'id' && (
              <div className="bg-amber-50 rounded-2xl p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">ID Required at Check-in{isUAE && <Ar className="mt-0.5">الهوية مطلوبة عند تسجيل الوصول</Ar>}</p>
                    <p className="text-xs text-amber-700 mt-1">Please bring a valid government-issued ID for verification.{isUAE && <Ar className="mt-0.5">يرجى إحضار هوية حكومية سارية للتحقق.</Ar>}</p>
                  </div>
                </div>
              </div>
            )}
            {successAction === 'reload' ? (
              <Button onClick={() => window.location.reload()} data-testid="waiver-success-done"
                className="w-full h-12 rounded-full text-white text-lg font-semibold" style={{ background: brandColor }}>
                Done{isUAE && <ArInline>تم</ArInline>} {countdown > 0 && `(${countdown}s)`}
              </Button>
            ) : (
              <Button onClick={resetAll} data-testid="waiver-success-sign-another"
                className="w-full h-12 rounded-full text-white text-lg font-semibold" style={{ background: brandColor }}>
                Sign Another Waiver{isUAE && <ArInline>توقيع إقرار آخر</ArInline>}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Main wizard ─────────────────────────────────────────────────────────────
  const progress = (step === 7 ? 6 : Math.min(step, 5)) / 6;

  return (
    <div className={wrapperCls}>
      {/* Branded header + progress (branding hidden in kiosk embeds — kiosk chrome has it) */}
      <div className="bg-white p-4 pb-3 sticky top-0 z-10">
        {!hideBranding && (
        <div className="max-w-md mx-auto flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={branding.name} className="h-10 w-auto max-w-[160px] object-contain" />
            ) : (
              <span className="font-semibold text-gray-900" style={{ color: brandColor }}>
                {branding.name || 'Sign Waiver'}
              </span>
            )}
          </div>
          {branding.waiverVersion && (
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">v{branding.waiverVersion}</span>
          )}
        </div>
        )}
        <div className="max-w-md mx-auto">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress * 100}%`, background: brandColor }} />
          </div>
        </div>
      </div>

      <div key={signerSession} ref={scrollBodyRef} className="flex-1 p-4 pb-24 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-md mx-auto">

          {/* STEP 1: Waiver agreement */}
          {step === 1 && (
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900">Liability Waiver{isUAE && <Ar className="text-lg mt-0.5">إقرار إخلاء المسؤولية</Ar>}</h2>
              </div>
              {!showWaiverText ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Please Review the Waiver{isUAE && <Ar className="text-base mt-0.5">يرجى مراجعة الإقرار</Ar>}</h3>
                  <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                    By proceeding, you acknowledge that we collect your signature and device information for legal enforceability.
                    {isUAE && <Ar className="mt-1.5">بالمتابعة، فإنك تقر بأننا نقوم بجمع توقيعك ومعلومات جهازك لضمان النفاذ القانوني.</Ar>}
                  </p>
                  <Button onClick={() => setShowWaiverText(true)} data-testid="waiver-view-btn"
                    className="w-full h-12 rounded-full text-white text-base font-semibold" style={{ background: brandColor }}>
                    I Agree &amp; View Waiver{isUAE && <ArInline>أوافق وأعرض الإقرار</ArInline>}
                  </Button>
                </div>
              ) : (
                <div>
                  {/* FULL-height waiver — no inner scrollbox. The guest scrolls the
                      page through the entire text; agree unlocks at the end. */}
                  <div className="bg-gray-50 rounded-2xl p-4 text-sm text-gray-700 leading-relaxed mb-4 whitespace-pre-wrap break-words" style={{ unicodeBidi: 'plaintext' }} data-testid="waiver-full-text">
                    {waiverText || 'Waiver text not configured. Please contact the operator.'}
                    <div ref={waiverEndRef} aria-hidden="true" style={{ height: 1 }} />
                  </div>
                  {!readToBottom && (
                    <p className="text-xs text-gray-400 mb-3 text-center" data-testid="waiver-scroll-hint">
                      Scroll to the end of the waiver to continue
                      {isUAE && <Ar className="mt-0.5">مرّر حتى نهاية الإقرار للمتابعة</Ar>}
                    </p>
                  )}
                  <label className={`flex items-start gap-3 mb-4 p-3 rounded-2xl transition-colors ${readToBottom ? 'cursor-pointer group bg-gray-50 hover:bg-blue-50/60' : 'bg-gray-50 opacity-50 cursor-not-allowed'}`}>
                    <input type="checkbox" checked={hasAgreedToWaiver} disabled={!readToBottom}
                      onChange={(e) => setHasAgreedToWaiver(e.target.checked)}
                      data-testid="waiver-agree-checkbox"
                      className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      I have read and agree to the <span className="font-semibold">Liability Waiver &amp; Assumption of Risk</span>
                      {isUAE && <Ar className="mt-1">لقد قرأت وأوافق على <span className="font-semibold">إقرار إخلاء المسؤولية وتحمّل المخاطر</span></Ar>}
                    </span>
                  </label>
                  <Button onClick={() => setStep(2)} disabled={!hasAgreedToWaiver || !readToBottom} data-testid="waiver-agree-btn"
                    className="w-full h-12 rounded-full text-white text-base font-semibold disabled:opacity-50"
                    style={{ background: hasAgreedToWaiver && readToBottom ? brandColor : '#9CA3AF' }}>
                    {isUAE ? <>I Agree<ArInline>أوافق على الإقرار</ArInline></> : 'I Agree to the Liability Waiver'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Name & signature */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Your Name &amp; Signature{isUAE && <Ar className="text-lg mt-0.5">اسمك وتوقيعك</Ar>}</h2>
              <p className="text-sm text-gray-600 mb-4">Draw your signature and type your full legal name{isUAE && <Ar className="mt-0.5">ارسم توقيعك واكتب اسمك القانوني الكامل</Ar>}</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Signature{isUAE && <> <span dir="rtl" lang="ar">&middot; التوقيع</span></>} <span className="text-red-500">*</span></label>
                <div className="border-2 border-dashed border-gray-200 rounded-2xl bg-white overflow-hidden">
                  <SignatureCanvas ref={signaturePadRef}
                    canvasProps={{ className: 'w-full h-28', style: { touchAction: 'none' } }} />
                </div>
                <button onClick={() => signaturePadRef.current?.clear()}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                  Clear Signature{isUAE && <ArInline>مسح التوقيع</ArInline>}
                </button>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Legal Name{isUAE && <> <span dir="rtl" lang="ar">&middot; الاسم القانوني الكامل</span></>} <span className="text-red-500">*</span>
                </label>
                <Input value={name} data-testid="waiver-name-input"
                  onChange={(e) => {
                    const s = sanitizeInput(e.target.value, MAX_NAME_LENGTH); setName(s);
                    if (s.length >= MIN_NAME_LENGTH) validateName(s);
                  }}
                  onBlur={() => name && validateName(name)}
                  placeholder={isUAE ? 'Enter your full name · أدخل اسمك الكامل' : 'Enter your full name'}
                  className={`h-12 text-base ${nameError ? 'border-red-500' : ''}`}
                  maxLength={MAX_NAME_LENGTH} />
                {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button data-testid="waiver-name-continue"
                  onClick={() => {
                    if (validateName(name)) {
                      setCapturedSignatureData(captureSignatureLightweight());
                      // 🚶 Kiosk gate (OTP off) / POS config with no contact channels:
                      // guest is standing right there — no OTP round-trip at the counter
                      if (kioskBooking || (posFields && !posShow('email') && !posShow('phone'))) {
                        setPendingSig({
                          verified: true,
                          verificationMethod: 'kiosk',
                          verifiedContact: kioskBooking?.email || kioskBooking?.phone || 'Kiosk walk-in',
                          phoneNumber: kioskBooking?.phone || '',
                          email: kioskBooking?.email || '',
                          idVerificationRequired: false,
                        });
                        setStep(7);
                      } else {
                        setStep(3);
                      }
                    }
                  }}
                  disabled={name.trim().length < MIN_NAME_LENGTH}
                  className="flex-1 h-12 rounded-full text-white text-base font-semibold disabled:opacity-50"
                  style={{ background: brandColor }}>
                  Continue{isUAE && <ArInline>متابعة</ArInline>}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: Contact details + verification — POS mode only shows the
              channels its config collects (hidden fields are never asked for) */}
          {step === 3 && (() => {
            const showEmailField = !posFields || posShow('email');
            const showPhoneField = !posFields || posShow('phone');
            const chanBits = [showEmailField && 'email', showPhoneField && (isUAE ? 'WhatsApp' : 'phone')].filter(Boolean);
            const sendReady = posFields
              ? ((showEmailField && isValidEmail(email)) || (showPhoneField && phoneValid))
              : (isValidEmail(email) && phoneValid);
            return (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Verify Your Identity{isUAE && <Ar className="text-lg mt-0.5">تحقق من هويتك</Ar>}</h2>
              <p className="text-sm text-gray-600 mb-4">Enter your contact details — we&rsquo;ll send you a 6-digit verification code.{isUAE && <Ar className="mt-0.5">أدخل بيانات الاتصال الخاصة بك — سنرسل لك رمز تحقق مكوَّنًا من 6 أرقام.</Ar>}</p>

              <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-blue-50 mb-5" data-testid="waiver-verify-banner">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-sm text-blue-900 leading-snug">
                  {chanBits.length === 2
                    ? <>Your code will be sent to your <span className="font-semibold">email</span> and <span className="font-semibold">{isUAE ? 'WhatsApp' : 'phone'}</span> — use whichever arrives first.</>
                    : <>Your code will be sent to your <span className="font-semibold">{chanBits[0]}</span>.</>}
                  {isUAE && <Ar className="mt-1">سيتم إرسال الرمز — استخدم أيهما يصل أولاً.</Ar>}
                </p>
              </div>

              {showEmailField && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address{isUAE && <> <span dir="rtl" lang="ar">&middot; البريد الإلكتروني</span></>} {(!posFields || posReq('email') || !showPhoneField) && <span className="text-red-500">*</span>}</label>
                <Input type="email" value={email} data-testid="waiver-email-input"
                  onChange={(e) => { setEmail(sanitizeInput(e.target.value.toLowerCase(), MAX_EMAIL_LENGTH)); setVerificationFailed(false); if (emailError) setEmailError(''); }}
                  onBlur={() => email && validateEmail(email)} placeholder="your@email.com"
                  className={`h-12 text-base ${emailError ? 'border-red-500' : ''}`} maxLength={MAX_EMAIL_LENGTH} autoComplete="email" />
                {emailError && <p className="mt-1 text-xs text-red-600">{emailError}</p>}
              </div>
              )}

              {showPhoneField && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">{phoneLabel}{isUAE && <> <span dir="rtl" lang="ar">&middot; رقم واتساب</span></>} {(!posFields || posReq('phone') || !showEmailField) && <span className="text-red-500">*</span>}</label>
                <div data-testid="waiver-phone-input">
                  <PhoneInput
                    value={phoneE164}
                    defaultCountry={isUAE ? 'AE' : 'US'}
                    onChange={(e164, meta) => { setPhoneE164(e164 || ''); setPhoneValid(!!meta?.isValid); setVerificationFailed(false); }}
                  />
                </div>
              </div>
              )}

              {verificationFailed && (
                <div className="bg-red-50 rounded-2xl p-3 mb-4">
                  <p className="text-sm text-red-900 font-medium">Failed to send verification codes</p>
                  <p className="text-xs text-red-700 mt-1">Check your details, then try again</p>
                </div>
              )}
              {verificationFailed && (
                <button onClick={() => { setSkippedVerification(true); setVerificationMethod('id'); setStep(6); }}
                  data-testid="waiver-id-fallback-step3"
                  className="w-full text-sm py-2.5 mb-4 text-orange-600 hover:bg-orange-50 rounded-full transition-colors">
                  Can&rsquo;t receive verification? Verify with ID instead{isUAE && <Ar className="mt-0.5">لم يصلك الرمز؟ يمكنك التحقق بالهوية بدلاً من ذلك</Ar>}
                </button>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(posFields ? 1 : 2)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button onClick={handleSendCode} disabled={sendingCode || !sendReady} data-testid="waiver-send-code"
                  className="flex-1 h-12 text-white text-base font-semibold disabled:opacity-50" style={{ background: brandColor }}>
                  {sendingCode ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Sending...</span> : <>Send Code{isUAE && <ArInline>إرسال الرمز</ArInline>}</>}
                </Button>
              </div>
            </div>
            );
          })()}

          {/* STEP 5: Enter OTP */}
          {step === 5 && (
            <div>
              <div className="mb-6 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Enter Verification Code{isUAE && <Ar className="text-lg mt-0.5">أدخل رمز التحقق</Ar>}</h2>
                  <p className="text-sm text-gray-600" data-testid="waiver-otp-sent-to">
                    6-digit code sent to{' '}
                    {sentChannels.includes('email') && <span className="font-medium break-all">{email}</span>}
                    {sentChannels.length === 2 && ' and '}
                    {(sentChannels.includes('sms') || sentChannels.includes('whatsapp')) && (
                      <span className="font-medium break-all">{fullPhone}{isUAE ? ' (WhatsApp)' : ''}</span>
                    )}
                    {sentChannels.length === 2 && <span className="text-gray-500"> — the code from either one works</span>}
                    {isUAE && <Ar className="mt-1 text-gray-500">تم إرسال رمز مكوَّن من 6 أرقام — يعمل الرمز من أي منهما</Ar>}
                  </p>
                </div>
                <button onClick={handleSendCode} disabled={resendCooldown > 0 || sendingCode} data-testid="waiver-resend-code"
                  className="mt-1 shrink-0 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed">
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : (isUAE ? <>Resend<Ar className="text-xs">إعادة الإرسال</Ar></> : 'Resend code')}
                </button>
              </div>
              <CodeInput
                value={otpCode}
                onChange={(code) => { setOtpCode(code); latestOtpRef.current = code; }}
                onComplete={(completed) => {
                  latestOtpRef.current = completed;
                  if (!signing && completed.length === 6) handleSign();
                }}
                disabled={signing}
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(3)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button onClick={handleSign} data-testid="waiver-sign-btn"
                  disabled={signing || otpCode.length !== 6}
                  className="flex-1 h-12 text-white text-base font-semibold hover:opacity-90 disabled:opacity-50"
                  style={{ background: brandColor }}>
                  {signing ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Signing...</span> : <>Sign Waiver{isUAE && <ArInline>توقيع الإقرار</ArInline>}</>}
                </Button>
              </div>
              <div className="mt-5 text-center text-sm text-gray-500">
                Can&rsquo;t receive the code?{isUAE && <> <span dir="rtl" lang="ar">لم يصلك الرمز؟</span></>}{' '}
                <button data-testid="waiver-id-fallback"
                  onClick={() => { setSkippedVerification(true); setVerificationMethod('id'); setStep(6); }}
                  className="font-medium text-blue-600 hover:text-blue-800 transition-colors">
                  Verify with ID{isUAE && <ArInline>التحقق بالهوية</ArInline>}
                </button>
              </div>
            </div>
          )}

          {/* STEP 6: ID verification fallback */}
          {step === 6 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <h2 className="text-xl font-bold text-amber-900">ID Verification Required{isUAE && <Ar className="text-base mt-0.5">مطلوب التحقق من الهوية</Ar>}</h2>
              </div>
              <div className="bg-amber-50 rounded-2xl p-4 mb-6">
                <p className="text-sm text-gray-800 mb-3">No problem! Our staff will verify your identity when you arrive.{isUAE && <Ar className="mt-1">لا مشكلة! سيتحقق موظفونا من هويتك عند وصولك.</Ar>}</p>
                <p className="text-sm text-gray-800 mb-3"><strong>Staff may take a photo of your government-issued ID.</strong> This serves as verification for the signed waiver.{isUAE && <Ar className="mt-1"><strong>قد يلتقط الموظفون صورة لهويتك الحكومية.</strong> يُعد ذلك تحققًا للإقرار الموقَّع.</Ar>}</p>
                <div className="flex items-start gap-2 pt-2 border-t border-amber-200">
                  <Shield className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-amber-900">Bring a valid ID (driver license, passport, or state ID){isUAE && <Ar className="mt-1">أحضر هوية سارية (رخصة قيادة أو جواز سفر أو بطاقة هوية إماراتية)</Ar>}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(skippedVerification ? 3 : 5)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button onClick={handleIdVerificationSign} disabled={signing} data-testid="waiver-id-sign-btn"
                  className="flex-1 h-12 bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold disabled:opacity-50">
                  {isUAE ? <>I Understand<ArInline>فهمت — متابعة</ArInline></> : 'I Understand — Continue'}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 7: Age range + marketing consent — final step */}
          {step === 7 && (
            <div data-testid="waiver-final-step">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">One Last Step{isUAE && <ArInline>خطوة أخيرة</ArInline>}</h2>
              </div>
              <p className="text-sm text-gray-600 mb-5">
                {pendingSig?.verified ? 'Identity verified.' : 'Almost done.'} Confirm your age range to complete your waiver.
                {isUAE && <Ar className="mt-0.5">{pendingSig?.verified ? 'تم التحقق من هويتك. ' : ''}حدّد فئتك العمرية لإكمال الإقرار.</Ar>}
              </p>
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Age Range{isUAE && <> <span dir="rtl" lang="ar">&middot; فئتك العمرية</span></>} <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {AGE_RANGES.map((r) => (
                    <button key={r} onClick={() => setAgeRange(r)} data-testid={`waiver-age-${r.replace(/[^a-zA-Z0-9]/g, '')}`}
                      className={`h-11 rounded-2xl text-sm font-semibold transition-colors ${ageRange === r ? 'text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                      style={ageRange === r ? { background: brandColor } : {}}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              {/* 🖥️ POS fields — email / phone / ID per the org's Walk-Ins config */}
              {posFields && posShow('email') && !posContactCollected && (
                <div className="mb-5" data-testid="waiver-pos-email-block">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email{isUAE && <> <span dir="rtl" lang="ar">&middot; البريد الإلكتروني</span></>} {posReq('email') ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs">(optional)</span>}
                  </label>
                  <Input type="email" value={posEmail} data-testid="waiver-pos-email-input"
                    onChange={(e) => setPosEmail(sanitizeInput(e.target.value.toLowerCase(), MAX_EMAIL_LENGTH))}
                    placeholder="your@email.com" className="h-12 text-base" maxLength={MAX_EMAIL_LENGTH} autoComplete="email" />
                </div>
              )}
              {posFields && posShow('phone') && !posContactCollected && (
                <div className="mb-5" data-testid="waiver-pos-phone-block">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {phoneLabel}{isUAE && <> <span dir="rtl" lang="ar">&middot; رقم الهاتف</span></>} {posReq('phone') ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs">(optional)</span>}
                  </label>
                  <PhoneInput
                    value={posPhone}
                    defaultCountry={isUAE ? 'AE' : 'US'}
                    onChange={(e164, meta) => { setPosPhone(e164 || ''); setPosPhoneValid(!!meta?.isValid); }}
                  />
                </div>
              )}
              {posFields && posShow('idNumber') && (
                <div className="mb-5" data-testid="waiver-pos-id-block">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ID / Passport Number{isUAE && <> <span dir="rtl" lang="ar">&middot; رقم الهوية / جواز السفر</span></>} {posReq('idNumber') ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs">(optional)</span>}
                  </label>
                  <Input value={posIdNumber} data-testid="waiver-pos-id-input"
                    onChange={(e) => setPosIdNumber(sanitizeInput(e.target.value, 120))}
                    placeholder={isUAE ? 'Emirates ID or passport number' : 'ID or passport number'}
                    className="h-12 text-base" maxLength={120} />
                </div>
              )}
              {(!posFields || posShow('nationality')) && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nationality{isUAE && <> <span dir="rtl" lang="ar">&middot; الجنسية</span></>}{posFields && posReq('nationality') && <> <span className="text-red-500">*</span></>}</label>
                <select
                  value={nationality}
                  onChange={(e) => setNationality(e.target.value)}
                  data-testid="waiver-nationality-select"
                  className="w-full h-12 rounded-2xl border border-gray-200 bg-white px-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                >
                  {NATIONALITIES.map((n) => (
                    <option key={n.code} value={n.code}>{n.flag} {n.name}</option>
                  ))}
                </select>
              </div>
              )}
              {/* Marketing consent — split by region:
                  US: email consent is notice-based (CAN-SPAM); SMS needs express opt-in (TCPA)
                  UAE: express opt-in for BOTH email and WhatsApp (PDPL)
                  POS: only shown when the config collects email (contact + name present) */}
              {(!posFields || posShow('email')) && (
              <div className="mb-6 space-y-2">
                {isUAE && (
                  <label className="flex items-start gap-3 cursor-pointer p-3.5 rounded-2xl bg-gray-50 hover:bg-blue-50/60 transition-colors">
                    <input type="checkbox" checked={emailOptIn} onChange={(e) => setEmailOptIn(e.target.checked)}
                      data-testid="waiver-optin-checkbox"
                      className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-[13px] text-gray-600 leading-relaxed">
                      Keep me in the loop — I agree that <span className="font-semibold text-gray-800">{branding.name || 'this operator'}</span> may keep my contact details and email me occasional updates and offers. Unsubscribe anytime.
                      <Ar className="mt-1">أوافق على أن تحتفظ <span className="font-semibold text-gray-800">{branding.name || 'هذه الشركة'}</span> ببيانات الاتصال الخاصة بي وأن ترسل لي تحديثات وعروضًا عبر البريد الإلكتروني من حين لآخر. يمكنني إلغاء الاشتراك في أي وقت.</Ar>
                    </span>
                  </label>
                )}
                <label className="flex items-start gap-3 cursor-pointer p-3.5 rounded-2xl bg-gray-50 hover:bg-blue-50/60 transition-colors">
                  <input type="checkbox" checked={smsOptIn} onChange={(e) => setSmsOptIn(e.target.checked)}
                    data-testid="waiver-sms-optin-checkbox"
                    className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-[13px] text-gray-600 leading-relaxed">
                    {isUAE ? (
                      <>Message me on WhatsApp — I agree that <span className="font-semibold text-gray-800">{branding.name || 'this operator'}</span> may send me occasional offers and updates on WhatsApp. Reply STOP to opt out anytime.
                      <Ar className="mt-1">أوافق على أن ترسل لي <span className="font-semibold text-gray-800">{branding.name || 'هذه الشركة'}</span> عروضًا وتحديثات عبر واتساب من حين لآخر. يمكنني الرد بكلمة STOP لإلغاء الاشتراك في أي وقت.</Ar></>
                    ) : (
                      <>Text me exclusive offers <span className="text-gray-400">(optional)</span> — I agree that <span className="font-semibold text-gray-800">{branding.name || 'this operator'}</span> may send me occasional offers and updates by text. Msg &amp; data rates may apply. Reply STOP to opt out anytime. Not a condition of purchase.</>
                    )}
                  </span>
                </label>
              </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(pendingSig?.verificationMethod === 'kiosk' ? 2 : pendingSig?.verificationMethod === 'id' ? 6 : 5)} className="w-20 h-12 border-gray-200">
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button onClick={finalizeSign} disabled={signing || !ageRange} data-testid="waiver-complete-btn"
                  className="flex-1 h-12 text-white text-base font-semibold hover:opacity-90 disabled:opacity-50"
                  style={{ background: brandColor }}>
                  {signing ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Signing...</span> : (isUAE ? <>Complete &amp; Sign<ArInline>إكمال وتوقيع</ArInline></> : 'Complete & Sign Waiver')}
                </Button>
              </div>
              <p className="mt-4 text-center text-[11px] text-gray-400" data-testid="waiver-consent-notice">
                {isUAE
                  ? <>By signing, you confirm your age range is accurate. Your consent choices are recorded with this waiver.
                    <Ar className="mt-1">بتوقيعك، فإنك تؤكد صحة فئتك العمرية. يتم تسجيل خيارات الموافقة الخاصة بك مع هذا الإقرار.</Ar></>
                  : <>By signing, you confirm your age range is accurate and agree that <span className="font-medium text-gray-500">{branding.name || 'this operator'}</span> may email you occasional updates and offers — unsubscribe anytime. Your consent choices are recorded with this waiver.</>}
              </p>
            </div>
          )}

        </div>
      </div>

      {layout === 'overlay' && onClose && (
        <button onClick={onClose} aria-label="Close" data-testid="waiver-close"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-20">
          ✕
        </button>
      )}
    </div>
  );
};

export default WaiverFlow;
